/**
 * Session-expiry banner.
 *
 * `csrf.ts` silently refreshes a stale token and replays the request, so the user never sees the
 * common case. This handles the case a refresh CANNOT fix: the session is gone and the host will
 * reject every write until they sign in again.
 *
 * The one hard requirement is DO NOT DESTROY THEIR WORK. A trader may have several hundred words
 * of trade thesis in the journal modal, and the instinctive fixes — reload the page, redirect to
 * /login — throw all of it away. So: sign-in opens in a NEW TAB, this page keeps its form state,
 * and when they come back the next save picks up the fresh token on its own.
 */
import { SESSION_EXPIRED_EVENT } from "./csrf.js";

const BANNER_ID = "sessionExpiredBanner";

function build(): HTMLElement {
  const banner = document.createElement("div");
  banner.id = BANNER_ID;
  banner.className = "session-expired-banner";
  banner.setAttribute("role", "alert");

  const text = document.createElement("span");
  text.textContent = "Your session expired, so this couldn't be saved. Nothing you typed is lost — sign in again, then press Save.";
  banner.appendChild(text);

  const signIn = document.createElement("a");
  signIn.href = "/login";
  // New tab, deliberately: navigating this one away would discard the unsaved form.
  signIn.target = "_blank";
  signIn.rel = "noopener";
  signIn.className = "btn btn-primary session-expired-signin";
  signIn.textContent = "Sign in";
  banner.appendChild(signIn);

  const dismiss = document.createElement("button");
  dismiss.type = "button";
  dismiss.className = "session-expired-dismiss";
  dismiss.setAttribute("aria-label", "Dismiss");
  dismiss.innerHTML = "&times;";
  dismiss.onclick = () => banner.remove();
  banner.appendChild(dismiss);

  return banner;
}

/** Show the banner (idempotent — repeated failures must not stack banners). */
export function showSessionExpired(): void {
  if (document.getElementById(BANNER_ID)) return;
  document.body.appendChild(build());
}

export function installSessionExpiryHandler(target: EventTarget = window): void {
  target.addEventListener(SESSION_EXPIRED_EVENT, () => showSessionExpired());
}
